/**
 * AutomationSubHeader — status badges + enable toggle under the record title.
 *
 * Registered as `components.subHeader` for `ahoy_event_automation` AND
 * `ahoy_validation_rule` (precedent: AccessBadgeSubHeader). Because the
 * builder panel renders the same RecordHeader, the builder header gets these
 * badges for free — this component absorbed and replaced the never-mounted
 * `BuilderStatusChip`.
 *
 * The states, in the order they are checked:
 *
 * - **Draft** — the record does not exist yet (builder create path, via
 *   RecordDraftContext's `isUnsaved`). Nothing runs; enabled is meaningless.
 * - **Not enforcing** (rules only) — `ahoy_health === 'errored'`. An errored
 *   rule is silently SKIPPED at load (`rules_loader.py` fails open), so the
 *   toggle still reads enabled while no records are being protected. This is
 *   the one state where the badge is telling the user something urgent, which
 *   is why it outranks On/Off. There is deliberately no equivalent for
 *   automations: their only failure record (`ahoy_automation_ran` errors) is
 *   purgeable bookkeeping, and a health chip that goes green when history is
 *   deleted would be dishonest.
 * - **Managed by Ahoy** — internal/website-managed automations. On/Off would
 *   imply a control this record deliberately does not offer.
 * - **On / Off** (automations), **Enforcing / Off** (rules) — `ahoy_enabled`.
 * - **+ Pending changes** — a saved record with a proposal waiting: the live
 *   version still runs as-is while a replacement waits. Both facts matter.
 *
 * The toggle renders only where flipping it can work: automations that are
 * saved and not managed. Rules keep their existing inline Enabled editor in
 * the overview — their `ahoy_enabled` is a boolean property with a different
 * write shape, and a second toggle wired differently is how the two drift.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { usePermissions } from '@/hooks/usePermissions';
import { REMINDER_AUTOMATION_QUERY_KEY } from '@/hooks/useReminderAutomationEnabled';
import { BadgeWithDot } from '@/lib/base/badges/badges';
import { AutomationEnabledToggle } from '@/lib/crm/automations/AutomationEnabledToggle';
import { deriveAutomationCapabilities } from '@/lib/crm/automations/capabilities';
import {
  REMINDER_AUTOMATION_SCRIPT_NAME,
  triggerReminderBackfill,
} from '@/lib/crm/automations/reminderBackfill';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { useRecordDraft } from '../contexts/RecordDraftContext';

const RULE_TYPE = 'ahoy_validation_rule';

/**
 * `ahoy_enabled` is a `select` of `"true"`/`"false"` on automations and a
 * `boolean` on rules, but BOTH arrive as strings on the wire — every existing
 * call site compares against `'true'`, and so does this.
 */
function propIsTrue(object: CrmObject, name: string): boolean {
  return (
    String(getPropertyValue<unknown>(object.properties?.[name])) === 'true'
  );
}

function StatusBadge({
  object,
  isRule,
  isManaged,
}: {
  object: CrmObject;
  isRule: boolean;
  isManaged: boolean;
}) {
  if (
    isRule &&
    getPropertyValue<string>(object.properties?.ahoy_health) === 'errored'
  ) {
    return (
      <BadgeWithDot size='sm' color='error'>
        Not enforcing
      </BadgeWithDot>
    );
  }
  if (isManaged) {
    return (
      <BadgeWithDot size='sm' color='brand'>
        Managed by Ahoy
      </BadgeWithDot>
    );
  }
  const enabled = propIsTrue(object, 'ahoy_enabled');
  const enabledLabel = isRule ? 'Enforcing' : 'On';
  return (
    <BadgeWithDot size='sm' color={enabled ? 'success' : 'gray'}>
      {enabled ? enabledLabel : 'Off'}
    </BadgeWithDot>
  );
}

export function AutomationSubHeader({ object }: { object: CrmObject }) {
  const draft = useRecordDraft();
  const { user } = useAuthInfo();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [isToggling, setIsToggling] = useState(false);

  const isRule = object.entity_type_name === RULE_TYPE;
  const caps = useMemo(() => deriveAutomationCapabilities(object), [object]);

  const enabled = propIsTrue(object, 'ahoy_enabled');
  const automationName = getPropertyValue<string>(
    object.properties?.ahoy_automation_name
  );
  // The record's OWN type, not a hard-coded one: this sub-header serves every
  // automation-kind record (event, scheduled, rule), and a toggle write stamped
  // with the wrong entity type is rejected by the CRM.
  const entityTypeName = object.entity_type_name;
  const canToggle = hasPermission(entityTypeName, 'ahoy_property_updated');

  const handleToggle = useCallback(
    async (isSelected: boolean) => {
      if (!user) {
        return;
      }
      setIsToggling(true);
      try {
        // Optimistic by default (FRONTEND_PATTERNS § Mutating CRM data): the
        // badge and toggle read from the store-backed object, so the flip is
        // visible immediately and rolls back with a toast on failure. The
        // settings section's blocking mutation is a separately-sanctioned
        // synchronous config write — this surface is not on that allowlist
        // and should not be.
        await submitEventsOptimistic({
          orgId: object.org_id,
          userId: user.id,
          entityTypeName,
          primaryEntityId: object.entity_id,
          fallbackSeq: object.last_event_seq ?? 0,
          build: builder =>
            builder.propertyUpdated(
              object.entity_id,
              entityTypeName,
              'ahoy_enabled',
              isSelected ? 'true' : 'false'
            ),
          errorToast: { message: () => "We couldn't update the automation" },
        });
        if (isSelected && automationName === REMINDER_AUTOMATION_SCRIPT_NAME) {
          await triggerReminderBackfill(object.org_id);
          queryClient.invalidateQueries({
            queryKey: ['crm', object.org_id, REMINDER_AUTOMATION_QUERY_KEY],
          });
        }
      } finally {
        setIsToggling(false);
      }
    },
    [
      user,
      object.org_id,
      object.entity_id,
      object.last_event_seq,
      entityTypeName,
      automationName,
      queryClient,
    ]
  );

  // Builder create path: nothing is saved, so neither status nor toggle is
  // meaningful — a single Draft chip from the first moment makes the later
  // transition to a real status read as progress.
  if (draft?.isUnsaved) {
    return (
      <div className='flex items-center gap-1.5 mt-1'>
        <BadgeWithDot size='sm' color='brand'>
          Draft
        </BadgeWithDot>
      </div>
    );
  }

  const showToggle = !isRule && !caps.isManaged;

  return (
    <div className='flex items-center gap-2 mt-1'>
      <StatusBadge object={object} isRule={isRule} isManaged={caps.isManaged} />
      {/* A saved record with a pending proposal is BOTH — running as-is now,
          with something different waiting. Hiding either half misleads. */}
      {draft !== null && (
        <BadgeWithDot size='sm' color='brand'>
          Pending changes
        </BadgeWithDot>
      )}
      {showToggle && (
        <AutomationEnabledToggle
          enabled={enabled}
          canToggle={canToggle}
          isBusy={isToggling}
          onChange={handleToggle}
        />
      )}
    </div>
  );
}
